import React from 'react';
import { Box, ToggleButtonGroup, ToggleButton, Tooltip, Typography } from '@mui/material';
import {
  DesktopMac as DesktopIcon,
  TabletMac as TabletIcon,
  PhoneAndroid as MobileIcon,
} from '@mui/icons-material';

// Device viewport configurations
const DEVICE_CONFIGS = {
  desktop: { width: '100%', height: '100%', label: 'Desktop', maxWidth: null },
  tablet: { width: '768px', height: '100%', label: 'iPad', maxWidth: '768px' },
  mobile: { width: '375px', height: '100%', label: 'Mobile', maxWidth: '375px' },
};

/**
 * DevicePreviewFrame wraps an iframe (or children) in a device-viewport-sized container.
 * It constrains the content width to simulate different screen sizes.
 */
const DevicePreviewFrame = ({ device = 'desktop', onDeviceChange, children }) => {
  const config = DEVICE_CONFIGS[device] || DEVICE_CONFIGS.desktop;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      {/* Device toggle toolbar */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        py: 1,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.50',
        flexShrink: 0,
      }}>
        <ToggleButtonGroup
          value={device}
          exclusive
          onChange={(e, val) => val && onDeviceChange(val)}
          size="small"
          aria-label="Device preview"
        >
          <ToggleButton value="desktop" aria-label="Desktop">
            <Tooltip title="Desktop (100%)">
              <DesktopIcon fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="tablet" aria-label="Tablet">
            <Tooltip title="Tablet (768px)">
              <TabletIcon fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="mobile" aria-label="Mobile">
            <Tooltip title="Mobile (375px)">
              <MobileIcon fontSize="small" />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">
          {config.label}{config.maxWidth ? ` — ${config.maxWidth}` : ''}
        </Typography>
      </Box>

      {/* Device frame container */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        overflow: 'auto',
        bgcolor: device === 'desktop' ? 'transparent' : 'grey.200',
        py: device === 'desktop' ? 0 : 2,
      }}>
        <Box sx={{
          width: config.width,
          maxWidth: config.maxWidth || '100%',
          height: '100%',
          bgcolor: 'white',
          boxShadow: device === 'desktop' ? 'none' : '0 0 20px rgba(0,0,0,0.15)',
          borderRadius: device === 'desktop' ? 0 : '12px',
          overflow: 'hidden',
          border: device === 'desktop' ? 'none' : '8px solid #1a1a1a',
          position: 'relative',
          transition: 'all 0.3s ease',
        }}>
          {/* Device bezel top bar for non-desktop */}
          {device !== 'desktop' && (
            <Box sx={{
              height: device === 'mobile' ? '28px' : '20px',
              bgcolor: '#1a1a1a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {device === 'mobile' && (
                <Box sx={{ width: '40%', height: '4px', bgcolor: '#333', borderRadius: '2px' }} />
              )}
            </Box>
          )}

          {/* Content area */}
          <Box sx={{
            height: device === 'desktop' ? '100%'
              : device === 'mobile' ? 'calc(100% - 28px)'
              : 'calc(100% - 20px)',
            overflow: 'hidden',
          }}>
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export { DEVICE_CONFIGS };
export default DevicePreviewFrame;
